<script setup lang="ts">
import type {PropType} from "vue";
import {formatCurrency, formatPercentage} from "@/lib/formatCurrencyNumbers.ts";
import {converterCurrencies, getSignFromQuote} from "@consts";

const props = defineProps({
  close: {
    type: Number as PropType<number | undefined>,
    required: false,
    default: undefined
  },
  priceChange24h: {
    type: Number as PropType<number | undefined>,
    required: false,
    default: undefined
  },
  priceChange7d: {
    type: Number as PropType<number | undefined>,
    required: false,
    default: undefined
  },
  vwap: {
    type: Number as PropType<number | undefined>,
    required: false,
    default: undefined
  },
  baseVolume: {
    type: Number as PropType<number | undefined>,
    required: false,
    default: undefined
  },
  quoteVolume: {
    type: Number as PropType<number | undefined>,
    required: false,
    default: undefined
  },
  quote: { type: String, required: true },
  crypto: { type: String, required: true },

  quoteName: { type: String, required: true },
  cryptoName: { type: String, required: true },
});

const sign = getSignFromQuote(props.quote)

</script>

<template>
  <div class="stats-container p-6 border-2 border-brand-stroke/10 rounded-4 bg-brand-background/90 rounded-lg text-white">
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
      <div class="stat-item">
        <span class="stat-label text-sm font-semibold">
          {{ $t('close_price') }} ({{ quoteName }})
        </span>
        <span class="stat-value text-2xl font-bold block mt-1">{{ formatCurrency(close,sign) }}</span>
      </div>
      <div class="stat-item">
        <span class="stat-label text-sm font-semibold">{{ $t('price_variation') }} (24h)</span>
        <span class="stat-value text-2xl font-bold block mt-1"
              :class="{'text-brand-primary': (priceChange24h??0) > 0, 'text-red-500': (priceChange24h??0) < 0}"
        >{{ formatPercentage(priceChange24h) }}</span>
      </div>
      <div class="stat-item">
        <span class="stat-label text-sm font-semibold">{{ $t('price_variation') }} (7g)</span>
        <span class="stat-value text-2xl font-bold block mt-1"
              :class="{'text-brand-primary': (priceChange7d??0) > 0, 'text-red-500': (priceChange7d??0) < 0}"
        >{{ formatPercentage(priceChange7d) }}</span>
      </div>
      <div class="stat-item">
        <span class="stat-label text-sm font-semibold">
          {{ $t('vwap') }} (VWAP)
        </span>
        <span class="stat-value text-2xl font-bold block mt-1">{{ formatCurrency(vwap,sign) }}</span>
      </div>
      <div class="stat-item">
        <span class="stat-label text-sm font-semibold">{{ $t('volume_last_24h') }} ({{ cryptoName }})</span>
        <span class="stat-value text-2xl font-bold block mt-1">{{ formatCurrency(baseVolume,sign) }}</span>
      </div>
      <div class="stat-item">
        <span class="stat-label text-sm font-semibold">
          {{ $t('volume_last_24h') }} ({{ quoteName }})
        </span>
        <span class="stat-value text-2xl font-bold block mt-1">{{ formatCurrency(quoteVolume,sign) }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.stats-container {
  transition: all 0.5s ease;
  color: white; /* Ensure text is white for better readability */
}

.stat-item {
  min-width: 150px;
}

.stat-label {
  margin-bottom: 4px;
}

.stat-value {
  margin-top: 4px;
}
</style>
